import { useAgents, type WireChatAgent, type WireChatListing } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { personParty, viewerOf } from '@/directory/explainKit';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainChatAgent, explainChatListing } from './explain';

/**
 * A chat already had, in the Past chats list (ADR-0155): the row that
 * reopens it, and under it what it was, with whom, and what to do with it.
 * Reopening stays the row's own press; the disclosure never reopens it.
 */
export function PastChatRow({ listing, onRestore }: { listing: WireChatListing; onRestore: () => void }) {
  const who = useWho();
  const me = useMeOrThrow();
  return (
    <li className="bg-raised">
      <button type="button" onClick={onRestore} className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-sunken">
        <span className="text-dense text-ink">{listing.title ?? 'Nothing said yet'}</span>
        <span className="text-label text-meta">
          {listing.agentName ?? 'Your twin'} · {new Date(listing.lastTurnAt ?? listing.startedAt).toLocaleString('en-GB')}
          {listing.closedAt ? ' · closed' : ''}
        </span>
      </button>
      <div className="px-4 pb-2">
        <Explainer context={explainChatListing(listing, who, viewerOf(me))} />
      </div>
    </li>
  );
}

/**
 * Who the person is about to talk to: what the twin or the agent is for,
 * who sponsors it where the platform shows that to this reader, and what
 * to ask it. Closed until asked; the picker above it still chooses.
 */
export function AgentAbout({ agent }: { agent: WireChatAgent }) {
  const who = useWho();
  const me = useMeOrThrow();
  const agents = useAgents().data?.agents;
  const known = agents?.find((a) => a.handle === agent.handle);
  const sponsor = known ? personParty(viewerOf(me), who, known.sponsor.id, known.sponsor.displayName, 'sponsors it') : null;
  return <Explainer label={`Who ${agent.givenName} is and what to ask`} context={explainChatAgent(agent, who, viewerOf(me), { sponsor })} />;
}
